import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
} from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import API from '../../services/api';
import CustomSnackBar from '../../components/CustomSnackBar';

const toISO = (v) => {
  if (!v) return '';
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    if (v.includes('T')) return v.split('T')[0];
  }
  try { const d = new Date(v); if (!isNaN(d)) return d.toISOString().split('T')[0]; } catch {}
  return '';
};

export default function AddProductToInvoice({ invoiceId, orderIds, supplierId, companyId, open, onAdded, setOpen }) {
  const [loading, setLoading] = useState(false);
  const [loadingRows, setLoadingRows] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({}); // product_id -> {quantity, price, prformat, dueDate}
  const [formats, setFormats] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!open) return;
      setLoadingRows(true);
      try {
        let items = [];
        if (Array.isArray(orderIds) && orderIds.length > 0) {
          const res = await API.get('order-Av/unified_products/', {
            params: { order_ids: orderIds.join(',') }
          });
          items = (res.data?.order_details || []).map(it => ({
            product: it.product,
            prformat: it.prformat,
            remaining: it.remaining_quantity ?? 0,
            listPrice: it.listPrice,
            finalPrice: it.finalPrice,
          }));
        }

        if (items.length === 0 && supplierId) {
          const payload = { supplier_id: Number(supplierId) };
          if (companyId) payload.company_id = Number(companyId);
          const productsRes = await API.post('product/getProductsBySupplier/', payload);
          items = (Array.isArray(productsRes.data) ? productsRes.data : [])
            .filter((p) => p?.is_active !== false)
            .map((p) => ({
              product: p,
              prformat: p?.prformat || null,
              remaining: p?.units ?? 0,
              listPrice: p?.prices?.[0]?.price ?? p?.listPrice ?? p?.finalPrice ?? 0,
              finalPrice: p?.prices?.[0]?.price ?? p?.finalPrice ?? p?.listPrice ?? 0,
            }));
        }

        setRows(items);
        // Cargar formatos (scoping correcto)
        try {
          const { data } = await API.get('invoice/catalogs/create_invoice/');
          setFormats(Array.isArray(data?.formats) ? data.formats : []);
        } catch { setFormats([]); }
      } catch (e) {
        console.error(e);
        setSnack({ open: true, message: 'Error cargando productos', severity: 'error' });
        setRows([]);
      } finally {
        setLoadingRows(false);
      }
    };
    load();
  }, [open, orderIds, supplierId, companyId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      String(r.product?.code || '').toLowerCase().includes(q) ||
      String(r.product?.description || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const updateField = (pid, field, value) => setForm(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [field]: value } }));

  const handleAdd = async (row) => {
    const pid = row.product?.id;
    const cfg = form[pid] || {};
    const qty = Number(cfg.quantity || 0);
    const price = Number(cfg.price || row.finalPrice || row.listPrice || 0);
    const prfObj = (cfg.prformat && typeof cfg.prformat === 'object') ? cfg.prformat : row.prformat;
    const prfId = cfg.prformat_id ?? prfObj?.id ?? null;
    const due = toISO(cfg.dueDate) || null;
    if (!qty || qty <= 0) {
      setSnack({ open: true, message: 'Ingrese cantidad válida', severity: 'warning' });
      return;
    }
    try {
      setLoading(true);
      await API.post('invoice-detail/', {
        invoice_id: invoiceId,
        product_id: pid,
        quantity: qty,
        quantityRe: qty,
        price: price,
        prformat_id: prfId,
        dueDate: due,
      });
      setSnack({ open: true, message: 'Producto agregado', severity: 'success' });
      if (onAdded) await onAdded();
      setOpen(false);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, message: 'No se pudo agregar', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        size='small'
        placeholder='Buscar por código o descripción'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{ startAdornment: (<InputAdornment position='start'><Search /></InputAdornment>) }}
      />
      <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 420 }}>
        <Table size='small' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 120 }}>Código</TableCell>
              <TableCell>Descripción</TableCell>
              <TableCell align='right' style={{ width: 120 }}>Disp.</TableCell>
              <TableCell align='right' style={{ width: 120 }}>Cantidad</TableCell>
              <TableCell align='right' style={{ width: 140 }}>Precio</TableCell>
              <TableCell style={{ width: 140 }}>Formato</TableCell>
              <TableCell style={{ width: 160 }}>Vence</TableCell>
              <TableCell align='center' style={{ width: 80 }}>Acción</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRows && (
              <TableRow><TableCell colSpan={8} align='center'>Cargando...</TableCell></TableRow>
            )}
            {!loadingRows && filtered.length === 0 && (
              <TableRow><TableCell colSpan={8} align='center'>Sin resultados</TableCell></TableRow>
            )}
            {filtered.map((r) => {
              const pid = r.product?.id;
              const cfg = form[pid] || {};
              return (
                <TableRow key={pid} hover>
                  <TableCell>{r.product?.code}</TableCell>
                  <TableCell>{r.product?.description}</TableCell>
                  <TableCell align='right'>{Number(r.remaining || 0).toLocaleString()}</TableCell>
                  <TableCell align='right'>
                    <TextField size='small' type='number' sx={{ width: 100 }} value={cfg.quantity || ''}
                      onChange={(e) => updateField(pid, 'quantity', e.target.value)} />
                  </TableCell>
                  <TableCell align='right'>
                    <TextField
                      size='small'
                      type='number'
                      sx={{ width: 120 }}
                      value={cfg.price ?? r.finalPrice ?? r.listPrice ?? ''}
                      onChange={(e) => updateField(pid, 'price', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size='small'
                      sx={{ width: 160 }}
                      value={cfg.prformat_id ?? r.prformat?.id ?? ''}
                      onChange={(e) => {
                        const id = e.target.value || null;
                        const selected = formats.find(f => String(f.id) === String(id)) || null;
                        setForm(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), prformat_id: id, prformat: selected } }));
                      }}
                    >
                      <MenuItem value=''>—</MenuItem>
                      {formats.map(f => (
                        <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField size='small' type='date' sx={{ width: 150 }} value={cfg.dueDate || ''}
                      onChange={(e) => updateField(pid, 'dueDate', e.target.value)} InputLabelProps={{ shrink: true }} />
                  </TableCell>
                  <TableCell align='center'>
                    <Tooltip title='Agregar'>
                      <span>
                        <IconButton color='primary' onClick={() => handleAdd(r)} disabled={loading}>
                          <Add />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomSnackBar
        open={snack.open}
        setOpen={(v) => setSnack(prev => ({ ...prev, open: v }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}
